import { I18n } from '@affine/i18n';
import {
  setSlashMenuItemTransform,
  type SlashMenuItem,
} from '@blocksuite/affine/widgets/slash-menu';

/**
 * Slash menu items whose display name is localized. The i18n key holds the
 * translated name; the English fallback stays in the item's `name`.
 */
const SLASH_MENU_LOCALIZED_NAMES: Record<string, string> = {
  'Calendar View': 'com.affine.slash-menu.calendar-view',
  'Kanban View': 'com.affine.slash-menu.kanban-view',
  'Table View': 'com.affine.slash-menu.table-view',
};

/**
 * Extra search aliases (Russian words and common misspellings) matched in
 * addition to `name` and the item's own `searchAlias`.
 */
const SLASH_MENU_SEARCH_ALIASES: Record<string, string[]> = {
  'Calendar View': ['календарь'],
  'Kanban View': ['канбан', 'доска'],
  'Table View': ['таблица', 'лфт', 'tablica'],
};

function isEnglishLocale(): boolean {
  return I18n.language?.startsWith('en') ?? true;
}

function localizeSlashMenuItem(item: SlashMenuItem): SlashMenuItem {
  const nameKey = SLASH_MENU_LOCALIZED_NAMES[item.name];
  const aliases = SLASH_MENU_SEARCH_ALIASES[item.name] ?? [];
  if (!nameKey && aliases.length === 0) return item;

  const searchAlias = [...new Set([...(item.searchAlias ?? []), ...aliases])];
  if (!nameKey) return { ...item, searchAlias };

  // `I18n[key]()` returns the key itself when the resource is not loaded
  // yet (lazy languages) — fall back to the English name in that case.
  const translated = isEnglishLocale() ? item.name : I18n[nameKey]();
  const name = translated === nameKey ? item.name : translated;
  return {
    ...item,
    name,
    // The original English name must stay searchable after localization.
    searchAlias: [item.name, ...searchAlias],
  };
}

/**
 * Registers the global slash menu item transform that localizes item names
 * (e.g. «Таблица» for Russian locale) and adds Russian search aliases.
 */
export function patchSlashMenu(): void {
  setSlashMenuItemTransform(localizeSlashMenuItem);
}
